import React, { useState } from 'react';

import { executeCallback } from 'core/utils/global-utils';
import { isJssField } from 'core/utils/sitecore-utils';
import { ICONS, Icon } from 'core/components/Icon';
import { bindKeyboardControls } from 'core/utils/accessibility-utils';
import UpdatedCtrlHolder from 'pods/forms/components/structural/UpdatedCtrlHolder';
import IProps from 'pods/forms/components/formFieldProps.d';

import * as S from './Text.style';

export const RawText = (props: any) => (
	<S.Input
		className={props.inputAttrs?.className ?? ''}
		type="text"
		{...props}
		disabled={props.uiDisabled}
	/>
);

export const TEXT_BTN_CLASS = 'text-button-within';

const Text = ({
	config,
	computed,
	htmlId,
	path,
	handleChangeFast,
	handleBlurFast,
	handleKeyPressFast,
	value,
	getDisplayError,
	renderTextNode,
	ctrlHolderProps,
}: IProps) => {
	const { data = {}, hasCloseIcon, enableButtonWithin } = config;

	const {
		inputAttrs = {},
		labelHelpText,
		disablePaste,
		appendMsg,
		bottomMsg,
		ctrlHolderFullWidth,
		readOnly,
		fullWidth,
		startAdornment,
		endAdornment,
		trackInput,
		trackShowClickEvent,
		handleChange,
		handleFocus,
		handleBlur,
		triggerError,
		uiDisabled,
		customClassName,
		handleClear,
		bottomMsgWithIcon,
	} = data;

	const label = isJssField(data.label)
		? data.label
		: renderTextNode(data.label, 'span');
	const helpText = labelHelpText
		? isJssField(labelHelpText)
			? labelHelpText
			: renderTextNode(labelHelpText, 'span')
		: labelHelpText;

	// Use label value if placeholder value not provided
	//TODO get field value placeholder if JSS
	const placeholder =
		data.placeholder ||
		(isJssField(data.label)
			? data.label.value?.props?.jss?.value ?? data.label.value
			: data.label);

	const tooltip = renderTextNode(data.tooltip);
	const errorMsg = getDisplayError();

	const { required, disabled } = computed;

	const _ctrlHolderProps = {
		htmlId,
		label,
		helpText,
		errorMsg,
		appendMsg,
		bottomMsg,
		value,
		disabled,
		uiDisabled,
		startAdornment,
		endAdornment,
		fullWidth,
		ctrlHolderFullWidth,
		required,
		readOnly,
		customClassName,
		enableButtonWithin,
		bottomMsgWithIcon,
		...(data.tooltip && { tooltip }),
		...ctrlHolderProps,
	};

	const hasError = !!errorMsg || triggerError;
	const hasValue = !!value;
	const hasHelpText = !!helpText;

	// For Password type input field
	const isPasswordType = inputAttrs?.type === 'password';
	const [isShowMasked, setIsShowMasked] = useState(isPasswordType);
	const showButtonWithin =
		enableButtonWithin && !disabled && !uiDisabled && !readOnly && hasValue;

	const handleCloseEvent = (e: React.MouseEvent<HTMLElement>) => {
		e?.stopPropagation();
		handleClear?.();
		hasCloseIcon ? handleChangeFast('') : setIsShowMasked(!isShowMasked);
		isShowMasked && executeCallback(trackShowClickEvent);
	};

	return (
		<UpdatedCtrlHolder {..._ctrlHolderProps}>
			<RawText
				id={htmlId}
				name={path}
				value={value ?? ''}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					executeCallback(trackInput);
					handleChange?.(e);
					handleChangeFast(e.target.value);
				}}
				onFocus={(e: React.ChangeEvent<HTMLInputElement>) => {
					if (!!e.target.placeholder) {
						e.target.placeholder = '';
					}
					handleFocus?.(e);
				}}
				onBlur={(e: React.ChangeEvent<HTMLInputElement>) => {
					if (!e.target.value) {
						e.target.placeholder = placeholder;
					}
					handleBlur?.(value);
					handleBlurFast();
				}}
				onPaste={(e: React.FormEvent<HTMLInputElement>) => {
					if (disablePaste) {
						e.preventDefault();
					}
				}}
				onKeyPress={handleKeyPressFast}
				aria-describedby={hasHelpText ? `${htmlId}-helpText` : undefined}
				readOnly={readOnly}
				hasStartAdornment={!!startAdornment}
				hasEndAdornment={!!endAdornment}
				{...{
					required,
					disabled,
					uiDisabled,
					hasError,
					hasValue,
					placeholder,
					fullWidth,
				}}
				{...inputAttrs}
				// For password type input, change type to 'text' to remove the mask on input when isShowMasked set to false
				{...(isPasswordType && !isShowMasked && { type: 'text' })}
			/>
			{showButtonWithin && (
				<S.ButtonWithin
					className={TEXT_BTN_CLASS}
					type="button"
					onKeyUp={bindKeyboardControls}
					onClick={e => handleCloseEvent(e)}
					onMouseDown={e => handleCloseEvent(e)}
					onMouseUp={e => handleCloseEvent(e)}
					hasCloseIcon={hasCloseIcon}
					hasValue={!!value}
				>
					{(hasCloseIcon && (
						<Icon
							name={ICONS.CLEAR}
							size={24}
							data-target-id={`text--clear-icon-${htmlId}`}
						/>
					)) || (
						<S.ShowHideLabel ctaType="cosmetic">
							{isShowMasked ? 'Show' : 'Hide'}
						</S.ShowHideLabel>
					)}
				</S.ButtonWithin>
			)}
		</UpdatedCtrlHolder>
	);
};

export default Text;
